import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiRequestsService } from '../../services/api-requests.service';

const STARTER_JSON = JSON.stringify({
    version: '6.1',
    screens: [
        {
            id: 'WELCOME',
            title: 'Welcome',
            terminal: true,
            layout: {
                type: 'SingleColumnLayout',
                children: [
                    { type: 'TextHeading', text: 'Hello!' },
                    { type: 'TextBody', text: 'Please fill in the details below.' },
                    {
                        type: 'Footer',
                        label: 'Submit',
                        'on-click-action': { name: 'complete', payload: {} },
                    },
                ],
            },
        },
    ],
}, null, 2);

const FLOW_CATEGORIES = [
    'SIGN_UP', 'SIGN_IN', 'APPOINTMENT_BOOKING', 'LEAD_GENERATION',
    'CONTACT_US', 'CUSTOMER_SUPPORT', 'SURVEY', 'OTHER',
];

interface WaFlow {
    _id: string;
    metaFlowId: string;
    flowName: string;
    categories: string[];
    status: 'DRAFT' | 'PUBLISHED' | 'DEPRECATED' | 'BLOCKED' | 'THROTTLED';
    flowJson: string;
    validationErrors: any[];
    createdAt: string;
}

@Component({
    selector: 'app-wa-flows',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './wa-flows.component.html',
    styleUrl: './wa-flows.component.scss',
})
export class WaFlowsComponent implements OnInit {
    readonly categories = FLOW_CATEGORIES;

    flows: WaFlow[] = [];
    isLoadingFlows = false;

    // Create form
    createName = '';
    selectedCategories: Set<string> = new Set(['SURVEY']);
    isCreating = false;
    createResult = '';
    createSuccess = false;

    // JSON editor
    editingFlow: WaFlow | null = null;
    editJson = '';
    isUploading = false;
    uploadResult = '';
    uploadSuccess = false;
    validationErrors: any[] = [];

    // Per-row action loading
    actionLoadingId: string | null = null;

    constructor(private apiService: ApiRequestsService) {}

    ngOnInit(): void { this.loadFlows(); }

    loadFlows(): void {
        this.isLoadingFlows = true;
        this.apiService.getApiCall('api/v1/flows').subscribe({
            next: (res: any) => {
                this.isLoadingFlows = false;
                this.flows = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
            },
            error: () => { this.isLoadingFlows = false; },
        });
    }

    toggleCategory(cat: string): void {
        const next = new Set(this.selectedCategories);
        if (next.has(cat)) {
            if (next.size > 1) next.delete(cat);
        } else {
            next.add(cat);
        }
        this.selectedCategories = next;
    }

    isCategorySelected(cat: string): boolean { return this.selectedCategories.has(cat); }

    createFlow(): void {
        if (!this.createName.trim() || this.isCreating) return;
        this.isCreating = true;
        this.createResult = '';
        this.apiService.postApiCall('api/v1/flows', {
            flowName: this.createName.trim(),
            categories: Array.from(this.selectedCategories),
        }).subscribe({
            next: (res: any) => {
                this.isCreating = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.createResult = `Flow "${this.createName.trim()}" created as DRAFT.`;
                    this.createSuccess = true;
                    this.createName = '';
                    this.selectedCategories = new Set<string>(['SURVEY']);
                    this.loadFlows();
                } else {
                    this.createResult = res?.errorDescription || 'Failed to create flow.';
                    this.createSuccess = false;
                }
            },
            error: (err: any) => {
                this.isCreating = false;
                this.createResult = err?.error?.errorDescription || 'Failed to create flow.';
                this.createSuccess = false;
            },
        });
    }

    startEditJson(flow: WaFlow): void {
        this.editingFlow = flow;
        this.editJson = flow.flowJson?.trim() ? flow.flowJson : STARTER_JSON;
        this.uploadResult = '';
        this.validationErrors = [];
        setTimeout(() => {
            document.getElementById('json-editor-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    }

    cancelEdit(): void {
        this.editingFlow = null;
        this.editJson = '';
        this.uploadResult = '';
        this.validationErrors = [];
        this.selectedScreenId = '';
    }

    // ── JSON Preview ─────────────────────────────────────────────────────────

    selectedScreenId = '';

    get parsedFlow(): any {
        try { return JSON.parse(this.editJson); } catch { return null; }
    }

    get previewScreens(): any[] {
        return this.parsedFlow?.screens || [];
    }

    get currentPreviewScreen(): any {
        if (!this.previewScreens.length) return null;
        return this.previewScreens.find(s => s.id === this.selectedScreenId)
            || this.previewScreens[0];
    }

    get previewBodyChildren(): any[] {
        const top: any[] = this.currentPreviewScreen?.layout?.children || [];
        const flat: any[] = [];
        for (const child of top) {
            if (child.type === 'Form') {
                for (const c of child.children || []) {
                    if (c.type !== 'Footer') flat.push(c);
                }
            } else if (child.type !== 'Footer') {
                flat.push(child);
            }
        }
        return flat;
    }

    get previewFooter(): any {
        const top: any[] = this.currentPreviewScreen?.layout?.children || [];
        for (const child of top) {
            if (child.type === 'Footer') return child;
            if (child.type === 'Form') {
                const f = (child.children || []).find((c: any) => c.type === 'Footer');
                if (f) return f;
            }
        }
        return null;
    }

    dataSource(child: any): any[] { return child?.['data-source'] || []; }

    uploadJson(): void {
        if (!this.editingFlow || this.isUploading) return;
        this.isUploading = true;
        this.uploadResult = '';
        this.validationErrors = [];
        this.apiService.postApiCall(`api/v1/flows/${this.editingFlow._id}/json`, {
            flowJson: this.editJson,
        }).subscribe({
            next: (res: any) => {
                this.isUploading = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    const { success, validationErrors } = res.Data || {};
                    this.validationErrors = validationErrors || [];
                    if (success && this.validationErrors.length === 0) {
                        this.uploadResult = 'JSON accepted — no validation errors.';
                        this.uploadSuccess = true;
                        // Update local copy so "Publish" button knows JSON is clean
                        const idx = this.flows.findIndex(f => f._id === this.editingFlow!._id);
                        if (idx >= 0) {
                            this.flows[idx] = { ...this.flows[idx], flowJson: this.editJson, validationErrors: [] };
                            this.editingFlow = this.flows[idx];
                        }
                    } else {
                        this.uploadResult = `JSON uploaded but has ${this.validationErrors.length} validation error(s).`;
                        this.uploadSuccess = false;
                    }
                } else {
                    this.uploadResult = res?.errorDescription || 'Upload failed.';
                    this.uploadSuccess = false;
                }
            },
            error: (err: any) => {
                this.isUploading = false;
                this.uploadResult = err?.error?.errorDescription || 'Upload failed.';
                this.uploadSuccess = false;
            },
        });
    }

    publishFlow(flow: WaFlow): void {
        if (this.actionLoadingId) return;
        if (!confirm(`Publish "${flow.flowName}"? This action cannot be undone.`)) return;
        this.actionLoadingId = flow._id;
        this.apiService.postApiCall(`api/v1/flows/${flow._id}/publish`, {}).subscribe({
            next: (res: any) => {
                this.actionLoadingId = null;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    const idx = this.flows.findIndex(f => f._id === flow._id);
                    if (idx >= 0) this.flows[idx] = { ...this.flows[idx], status: 'PUBLISHED' };
                    if (this.editingFlow?._id === flow._id) this.cancelEdit();
                } else {
                    alert(res?.errorDescription || 'Publish failed.');
                }
            },
            error: (err: any) => {
                this.actionLoadingId = null;
                alert(err?.error?.errorDescription || 'Publish failed.');
            },
        });
    }

    syncFlow(flow: WaFlow): void {
        if (this.actionLoadingId) return;
        this.actionLoadingId = flow._id;
        this.apiService.getApiCall(`api/v1/flows/${flow._id}/sync`).subscribe({
            next: (res: any) => {
                this.actionLoadingId = null;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    const { status } = res.Data || {};
                    const idx = this.flows.findIndex(f => f._id === flow._id);
                    if (idx >= 0) this.flows[idx] = { ...this.flows[idx], status };
                } else {
                    alert(res?.errorDescription || 'Sync failed.');
                }
            },
            error: (err: any) => {
                this.actionLoadingId = null;
                alert(err?.error?.errorDescription || 'Sync failed.');
            },
        });
    }

    deprecateFlow(flow: WaFlow): void {
        if (this.actionLoadingId) return;
        if (!confirm(`Deprecate "${flow.flowName}"? The flow will stop working for users.`)) return;
        this.actionLoadingId = flow._id;
        this.apiService.postApiCall(`api/v1/flows/${flow._id}/deprecate`, {}).subscribe({
            next: (res: any) => {
                this.actionLoadingId = null;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.flows = this.flows.filter(f => f._id !== flow._id);
                    if (this.editingFlow?._id === flow._id) this.cancelEdit();
                } else {
                    alert(res?.errorDescription || 'Deprecate failed.');
                }
            },
            error: (err: any) => {
                this.actionLoadingId = null;
                alert(err?.error?.errorDescription || 'Deprecate failed.');
            },
        });
    }

    copyFlowId(metaFlowId: string): void {
        navigator.clipboard.writeText(metaFlowId).then(() => {
            // brief visual feedback handled in template
        });
        this.copiedId = metaFlowId;
        setTimeout(() => { if (this.copiedId === metaFlowId) this.copiedId = null; }, 2000);
    }

    copiedId: string | null = null;

    statusClass(status: string): string {
        const map: Record<string, string> = {
            DRAFT: 'bg-secondary',
            PUBLISHED: 'bg-success',
            DEPRECATED: 'bg-warning text-dark',
            BLOCKED: 'bg-danger',
            THROTTLED: 'bg-danger',
        };
        return map[status] ?? 'bg-secondary';
    }

    hasCleanJson(flow: WaFlow): boolean {
        return !!flow.flowJson?.trim() && (flow.validationErrors?.length ?? 0) === 0;
    }

    trackById(_: number, item: WaFlow): string { return item._id; }
}
